import React from 'react';
import styled, { css } from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { Dictionary, IconComponent, IndicatorLayoutComponentProps, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { useIcons } from '@shapeable/icons';
import { useLang } from '@shapeable/ui';

const cls = classNames('indicator-layout-title-icon');

// -------- Props -------->

export type IndicatorLayoutTitleIconProps = IndicatorLayoutComponentProps & {};

const IndicatorLayoutTitleIconDefaultProps: Omit<IndicatorLayoutTitleIconProps, 'entity'> = {};

// -------- Child Component Props -------->

type ColorProps = {
  _color?: string;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    color: inherit;
    display: flex;
    flex-direction: column;
    height: 100%;
    font-family: ${theme.FONT('serif')};
  `,
});

const LayoutStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: ${theme.UNIT(5)} ${theme.UNIT(5)} ${theme.UNIT(6)} ${theme.UNIT(5)};
    border-top: 1px solid ${theme.COLOR('canvas')};

    ${({ _color }: ColorProps) => css`
      color: ${theme.COLOR(_color)};
    `}
  `,
  tablet: css`
    border-top: none;
    border-left: 1px solid ${theme.COLOR('canvas')};
    min-height: 280px;
    padding: ${theme.UNIT(5)} ${theme.UNIT(8)} ${theme.UNIT(8)} ${theme.UNIT(5)};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('strong')};
    margin: 0;
    font-size: ${28/16}em;
    font-weight: 700;
    line-height: 1.25;
    max-width: 14em;
  `,
});

const IconAreaStyles = breakpoints({
  base: css`
    display: flex;
    align-items: flex-start;
    margin-top: ${theme.UNIT(6)};
  `,
});

const IconContainerStyles = breakpoints({
  base: css`
    display: flex;
    align-items: flex-end;
    line-height: 0;
    flex-shrink: 0;
  `,
});

// -------- Components -------->

const iconRevealTransition = {
  duration: 0.95,
  ease: [0.22, 1, 0.36, 1],
};

const My = {
  Container: styled.div`${ContainerStyles}`,
  Layout: styled.div<ColorProps>`${LayoutStyles}`,
  Title: styled.h4`${TitleStyles}`,
  IconArea: styled.div`${IconAreaStyles}`,
  IconContainer: styled(motion.div)`${IconContainerStyles}`,
};

export const IndicatorLayoutTitleIcon: Shapeable.FC<IndicatorLayoutTitleIconProps> = (props) => {
  const { className, children, entity } = props;

  const { icon, color, title } = entity;
  const t = useLang();

  const icons = useIcons() as Dictionary<IconComponent>;
  const Icon = icon?.component && icons[icon?.component];

  const prefersReducedMotion = useReducedMotion();
  const colorValue = color?.value || 'strong';

  return (
    <My.Container className={cls.name(className)}>
      <My.Layout _color={colorValue}>
        {!!title && <My.Title>{t(title)}</My.Title>}
        {!!Icon && (
          <My.IconArea>
            <My.IconContainer
              initial={prefersReducedMotion ? false : { opacity: 0, y: 48 }}
              whileInView={
                prefersReducedMotion
                  ? undefined
                  : { opacity: 1, y: 0 }
              }
              viewport={{ once: true, amount: 0.35 }}
              transition={iconRevealTransition}
            >
              <Icon colors={{ fill: 'strong' }} />
            </My.IconContainer>
          </My.IconArea>
        )}
      </My.Layout>
      {children}
    </My.Container>
  );
};

IndicatorLayoutTitleIcon.defaultProps = IndicatorLayoutTitleIconDefaultProps;
IndicatorLayoutTitleIcon.cls = cls;
